import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import addNewTextToPageOperation from 'editor/src/store/design/operation/addNewTextToPageOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText } from 'editor/src/store/design/types';
import { appendToTextFlowChain, getTextFlowChains } from 'editor/src/store/design/util/textFlow';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import getTextLibraryAppendTarget from '../selector/getTextLibraryAppendTarget';
import { TextLibraryEntry } from '../types';
import buildTextLibraryText from '../util/buildTextLibraryText';

// a blank line between entries, as between verses
const ENTRY_SEPARATOR = '\n\n';
// in a text with section spacing, two blank lines: a section break, so the gap between entries
// (hymns) takes the section spacing and the gap between verses the paragraph spacing
const SECTION_ENTRY_SEPARATOR = '\n\n\n';

/**
 * Adds the entry's chosen verses (and title, unless left out) as a plain copy, not linked to the library.
 *
 * With one editable text selected, the entry goes after its content: the text keeps its own frame and style, so a
 * template's prepared frame fills up entry by entry (a fixed frame then shows the warning icon when full).
 * A linked frame takes it at the end of its chain. Otherwise it becomes a new text on the current page.
 * Returns whether the text was placed (the current page may not allow new elements).
 */
const insertTextLibraryEntryOperation =
  (entry: TextLibraryEntry, verseIndexes: number[], includeTitle = true): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const text = buildTextLibraryText(entry, verseIndexes, includeTitle);
    const target = getTextLibraryAppendTarget(state);
    const address = target && getStructureIndexByElementUuid(state.design.designData, target.uuid);

    // a number, as on the canvas: a server round trip can leave an unset spacing null
    const separator = typeof target?.extra.sectionSpacing === 'number' ? SECTION_ENTRY_SEPARATOR : ENTRY_SEPARATOR;
    const chain =
      target?.extra.textFlow &&
      getTextFlowChains(state.design.designData).find((members) =>
        members.some(({ element }) => element.uuid === target.uuid),
      );

    if (chain) {
      batch(() => {
        dispatch(saveUndoRedoStateOperation(MediaUpdateActionName.TEXT_UPDATED));
        appendToTextFlowChain(chain, text, separator).forEach(({ address: elementAddress, update }) =>
          dispatch(updateMediaElementAction({ elementAddress, elementUpdate: update })),
        );
      });
    } else if (target && address) {
      const current = target.extra.text.trimEnd();
      const elementUpdate: Partial<MediaText> = {
        extra: { ...target.extra, text: current ? `${current}${separator}${text}` : text },
      };
      // the caller measures; the operation keeps a fixed frame's height
      updateTextPropertiesWithoutRender(target, elementUpdate, undefined);
      dispatch(updateMediaElementOperation(address, elementUpdate, MediaUpdateActionName.TEXT_UPDATED));
    } else {
      const spreadIndex = getCurrentSpreadIndex(state);
      const elementUuid: number | undefined = dispatch(
        addNewTextToPageOperation(spreadIndex, 0, text, undefined, { keepFullText: true, enterTextEditMode: false }),
      );
      if (elementUuid === undefined) {
        return false;
      }
    }

    sendPostMessage('log.textLibraryEntryInserted', {
      id: entry.id,
      number: entry.number,
      verseCount: verseIndexes.length,
      appended: !!(target && address),
    });
    return true;
  };

export default insertTextLibraryEntryOperation;
